const PLACEHOLDERS = new Set([
  "", "YOUR_API_KEY", "your_api_key_here", "YOUR_APP_ID",
  "YOUR_RECAPTCHA_KEY", "your-project-id",
]);
export const CANONICAL_FIREBASE_PROJECT_ID = "navig8atlas";
export const CANONICAL_FIREBASE_AUTH_DOMAIN = "navig8atlas.firebaseapp.com";
export const CANONICAL_FIREBASE_STORAGE_BUCKET = "navig8atlas.firebasestorage.app";
const E2E_LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

function currentHostname() {
  return typeof window !== "undefined" ? window.location.hostname : "";
}

export function isE2ERuntimeEnv(env = process.env) {
  return env.REACT_APP_ENV === "e2e" && env.REACT_APP_E2E === "true";
}

// Emulator lane: the app talks to the local Firebase emulators (Auth, Firestore,
// Storage, Functions) under the `demo-navig8` project. No real credentials are
// involved, so it is only honoured on loopback hosts and never in production
// builds. Cloud agents and the emulator e2e job run in this lane so the
// "configured" code paths (Storage scans, callables, rules) are exercised.
export const EMULATOR_PROJECT_ID = "demo-navig8";
export const EMULATOR_PORTS = Object.freeze({ auth: 9099, firestore: 8080, storage: 9199, functions: 5001 });

export function isEmulatorRuntimeEnv(env = process.env, hostname = currentHostname()) {
  if (env.REACT_APP_FIREBASE_EMULATORS !== "true") return false;
  if (env.REACT_APP_ENV === "production") return false;
  // jsdom / SSR have no hostname; treat as loopback for tests.
  return !hostname || E2E_LOOPBACK_HOSTS.has(hostname);
}

/**
 * True when env vars look like a real project (not .env.example placeholders).
 * Lives here (no Firebase SDK imports) so pure services can consult it without
 * initializing Firebase; config.js re-exports it for existing consumers.
 */
export function isNavig8FirebaseConfigured(env = process.env) {
  if (isE2ERuntimeEnv(env)) return false;
  if (isEmulatorRuntimeEnv(env)) return true;
  const pid = env.REACT_APP_FIREBASE_PROJECT_ID;
  const key = env.REACT_APP_FIREBASE_API_KEY;
  const appId = env.REACT_APP_FIREBASE_APP_ID;
  if (!pid || pid === "your-project-id") return false;
  if (!key || key === "YOUR_API_KEY" || key === "your_api_key_here") return false;
  if (!appId || appId === "YOUR_APP_ID" || /^1:123456789:web:/.test(appId)) return false;
  return true;
}

export function productionFirebaseConfigErrors(env = process.env, hostname = currentHostname()) {
  if (env.REACT_APP_ENV === "e2e") {
    const errors = [];
    if (env.REACT_APP_E2E !== "true") {
      errors.push("REACT_APP_E2E must equal true for an E2E build.");
    }
    if (!E2E_LOOPBACK_HOSTS.has(hostname)) {
      errors.push("E2E builds may only run on a loopback hostname.");
    }
    return errors;
  }
  if (env.REACT_APP_ENV !== "production") return [];
  const errors = [];
  if (env.REACT_APP_FIREBASE_EMULATORS === "true") {
    errors.push("REACT_APP_FIREBASE_EMULATORS must not be set in a production build.");
  }
  const expected = env.REACT_APP_EXPECTED_FIREBASE_PROJECT_ID;
  const projectId = env.REACT_APP_FIREBASE_PROJECT_ID;
  const authDomain = env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  const storageBucket = env.REACT_APP_FIREBASE_STORAGE_BUCKET;
  const required = [
    ["REACT_APP_EXPECTED_FIREBASE_PROJECT_ID", expected],
    ["REACT_APP_FIREBASE_PROJECT_ID", projectId],
    ["REACT_APP_FIREBASE_API_KEY", env.REACT_APP_FIREBASE_API_KEY],
    ["REACT_APP_FIREBASE_APP_ID", env.REACT_APP_FIREBASE_APP_ID],
    ["REACT_APP_RECAPTCHA_ENTERPRISE_KEY", env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY],
  ];
  required.forEach(([name, value]) => {
    if (!value || PLACEHOLDERS.has(value)) errors.push(`${name} is missing or placeholder.`);
  });
  if (expected !== CANONICAL_FIREBASE_PROJECT_ID
      || projectId !== CANONICAL_FIREBASE_PROJECT_ID) {
    errors.push(`Production Firebase project must equal ${CANONICAL_FIREBASE_PROJECT_ID}.`);
  }
  if (authDomain !== CANONICAL_FIREBASE_AUTH_DOMAIN) {
    errors.push(`Firebase Auth domain must equal ${CANONICAL_FIREBASE_AUTH_DOMAIN}.`);
  }
  if (storageBucket !== CANONICAL_FIREBASE_STORAGE_BUCKET) {
    errors.push(`Firebase Storage bucket must equal ${CANONICAL_FIREBASE_STORAGE_BUCKET}.`);
  }
  return errors;
}

export function assertProductionFirebaseConfig(env = process.env, hostname = currentHostname()) {
  const errors = productionFirebaseConfigErrors(env, hostname);
  if (errors.length) {
    throw new Error(`Unsafe Firebase runtime configuration: ${errors.join(" ")}`);
  }
}
